import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { CheckCheck, Eye, Repeat } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '@/api/client';
import {
  useDeliveries,
  useDeliveryDetail,
  useReplayDelivery,
  useResolveDelivery,
} from '@/api/queries';
import type { WebhookDeliveryStatus } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { DeliveryStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const STATUSES: WebhookDeliveryStatus[] = ['pending', 'delivered', 'retrying', 'dlq', 'resolved'];

export const Route = createFileRoute('/_authed/webhooks/deliveries')({
  component: Deliveries,
  validateSearch: (raw): { status?: WebhookDeliveryStatus } => {
    const s = (raw as { status?: string }).status;
    return s && (STATUSES as string[]).includes(s) ? { status: s as WebhookDeliveryStatus } : {};
  },
});

function Deliveries() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const list = useDeliveries(search.status, 15_000);
  const replay = useReplayDelivery();
  const resolve = useResolveDelivery();
  const [detailId, setDetailId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('webhook_deliveries.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('webhook_deliveries.subtitle')}</p>
        </div>
        <div className="w-full sm:w-auto">
          <span className="block text-xs font-medium text-muted-foreground mb-1">
            {t('webhook_deliveries.filter_status')}
          </span>
          <Select
            value={search.status ?? '__all'}
            onValueChange={(v) =>
              navigate({ search: v === '__all' ? {} : { status: v as WebhookDeliveryStatus } })
            }
          >
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t('common.all')}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('webhook_deliveries.cols.created')}</TableHead>
              <TableHead>{t('webhook_endpoints.cols.url')}</TableHead>
              <TableHead>{t('webhook_deliveries.cols.attempt')}</TableHead>
              <TableHead>{t('webhook_deliveries.cols.status')}</TableHead>
              <TableHead>{t('webhook_deliveries.cols.response')}</TableHead>
              <TableHead>{t('webhook_deliveries.cols.next_attempt')}</TableHead>
              <TableHead className="w-44" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-mono text-xs whitespace-nowrap">
                  {dayjs(d.created_at).format('MMM D HH:mm:ss')}
                </TableCell>
                <TableCell className="font-mono text-xs">{d.endpoint_id.slice(0, 8)}…</TableCell>
                <TableCell className="tabular-nums">{d.attempt}</TableCell>
                <TableCell>
                  <DeliveryStatusBadge status={d.status} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {d.response_code != null ? (
                    <>
                      <span className="font-mono">{d.response_code}</span>
                      {d.response_body_excerpt && (
                        <span className="ms-2 line-clamp-1 inline-block max-w-[20ch] align-middle">
                          {d.response_body_excerpt}
                        </span>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {dayjs(d.next_attempt_at).format('MMM D HH:mm:ss')}
                </TableCell>
                <TableCell className="text-end space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDetailId(d.id)}
                    aria-label="View"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      replay.isPending || d.status === 'delivered' || d.status === 'resolved'
                    }
                    onClick={() =>
                      replay.mutate(d.id, {
                        onSuccess: () => toast.success(t('webhook_deliveries.replayed_toast')),
                        onError: (e) =>
                          toast.error(
                            e instanceof ApiError
                              ? e.message
                              : (t('webhook_deliveries.replay_failed') as string),
                          ),
                      })
                    }
                  >
                    <Repeat className="h-3.5 w-3.5" /> {t('webhook_deliveries.replay')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={resolve.isPending || d.status === 'resolved'}
                    onClick={() =>
                      resolve.mutate(d.id, {
                        onSuccess: () => toast.success(t('webhook_deliveries.resolved_toast')),
                        onError: (e) =>
                          toast.error(
                            e instanceof ApiError
                              ? e.message
                              : (t('webhook_deliveries.resolve_failed') as string),
                          ),
                      })
                    }
                  >
                    <CheckCheck className="h-3.5 w-3.5" /> {t('webhook_deliveries.resolve')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!list.isLoading && list.data?.length === 0 && (
          <Empty title={t('webhook_deliveries.none')} className="border-0" />
        )}
      </div>

      <DeliveryDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function DeliveryDetailDialog({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useDeliveryDetail(id);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">{id}</DialogTitle>
          <DialogDescription>{t('webhook_deliveries.title')}</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <div className="space-y-4 text-sm">
            <Row label="Status" value={data.status} />
            <Row label="Endpoint" value={data.endpoint_url} mono />
            <Row label="Active" value={data.endpoint_active ? '✓' : '✗'} />
            <Row label="Attempt" value={String(data.attempt)} />
            <Row label="Event type" value={data.event_type ?? '—'} mono />
            <Row label="Payment id" value={data.event_payment_id ?? '—'} mono />
            <Row
              label="Response"
              value={
                data.response_code != null
                  ? `${data.response_code}${
                      data.response_body_excerpt ? ` — ${data.response_body_excerpt}` : ''
                    }`
                  : '—'
              }
              wrap
            />
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Request body
              </div>
              <pre className="mt-1 max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-2 text-xs">
                <code>{data.request_body || '—'}</code>
              </pre>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Request headers
              </div>
              <pre className="mt-1 rounded-md border border-border bg-muted/30 p-2 text-xs">
                <code>
                  {Object.entries(data.request_headers)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join('\n')}
                </code>
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({
  label,
  value,
  mono,
  wrap,
}: {
  label: string;
  value: string;
  mono?: boolean;
  wrap?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={
          (mono ? 'font-mono text-xs ' : 'text-sm ') + (wrap ? 'break-all' : 'truncate')
        }
      >
        {value}
      </div>
    </div>
  );
}
